import React, { useEffect, useRef, useState } from "react";

const ZEGO_CDN =
  "https://unpkg.com/@zegocloud/zego-uikit-prebuilt/zego-uikit-prebuilt.js";

const Room = ({ roomId, userName = "Doctor" }) => {
  const containerRef = useRef(null);
  const zpRef = useRef(null);
  const [zegoReady, setZegoReady] = useState(!!window.ZegoUIKitPrebuilt);
  const [joinError, setJoinError] = useState("");
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    if (window.ZegoUIKitPrebuilt) {
      setZegoReady(true);
      return;
    }
    const existing = document.querySelector(`script[src="${ZEGO_CDN}"]`);
    if (existing) {
      const onLoad = () => setZegoReady(true);
      existing.addEventListener("load", onLoad);
      return () => existing.removeEventListener("load", onLoad);
    }
    const script = document.createElement("script");
    script.src = ZEGO_CDN;
    script.async = true;
    script.onload = () => setZegoReady(true);
    script.onerror = () => {
      console.error("ZEGOCLOUD CDN failed to load");
      setJoinError("Failed to load video call library. Please check your internet connection and try again.");
    };
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!zegoReady || !containerRef.current || !roomId) {
      if (zegoReady && roomId) setJoining(false);
      return;
    }
    const ZegoUIKitPrebuilt = window.ZegoUIKitPrebuilt;
    if (!ZegoUIKitPrebuilt) return;

    // getUserMedia (camera/mic) is only available in a secure context — exactly "localhost"/
    // "127.0.0.1" or HTTPS. A custom dev hostname like localhost.dev.com over plain HTTP is
    // NOT treated as secure, so ZegoCloud silently fails to render into the container.
    if (!window.isSecureContext) {
      setJoinError(
        `Video call requires a secure connection (HTTPS or "localhost"). This page is loaded over an insecure origin (${window.location.origin}), so the browser blocks camera/microphone access.`
      );
      return;
    }

    setJoinError("");
    setJoining(true);

    try {
      const appID = Number(process.env.REACT_APP_ZEGO_APP_ID);
      const serverSecret = process.env.REACT_APP_ZEGO_SERVER_SECRET;

      console.log("ZEGO Config - AppID:", appID, "ServerSecret length:", serverSecret?.length, "RoomID:", roomId);

      if (!appID || !serverSecret) {
        throw new Error("ZEGO App ID or Server Secret is not configured. Please check your .env file.");
      }

      if (appID === 0 || isNaN(appID)) {
        throw new Error(`Invalid ZEGO App ID: ${appID}. Expected a number.`);
      }

      const userID = `doctor-${Date.now()}`;
      console.log("Generating ZEGO token with userID:", userID, "userName:", userName);
      const kitToken = ZegoUIKitPrebuilt.generateKitTokenForTest(
        appID,
        serverSecret,
        roomId,
        userID,
        userName
      );
      console.log("Token generated successfully, length:", kitToken?.length);

      const zp = ZegoUIKitPrebuilt.create(kitToken);
      zpRef.current = zp;

      const joinPromise = zp.joinRoom({
        container: containerRef.current,
        scenario: { mode: ZegoUIKitPrebuilt.VideoConference },
        showScreenSharingButton: true,
        showTurnOffRemoteCameraButton: true,
        showTurnOffRemoteMicrophoneButton: true,
        showMyCameraToggleButton: true,
        showMyMicrophoneToggleButton: true,
        showAudioVideoSettingsButton: true,
        showTextChat: true,
        showUserList: true,
        maxUsers: 6,
        turnOnCameraWhenJoining: true,
        turnOnMicrophoneWhenJoining: true,
        showPreJoinView: false,
        showLeavingView: false,
      });

      if (joinPromise && typeof joinPromise.then === 'function') {
        joinPromise.then(() => {
          console.log("Successfully joined ZEGO room:", roomId);
          setJoining(false);
        }).catch((err) => {
          console.error("ZEGOCLOUD joinRoom failed:", err);
          setJoinError(err?.message || "Failed to start the video call. Check camera/microphone permissions and network connection.");
          setJoining(false);
        });
      } else {
        console.log("ZEGOCLOUD joinRoom called (no promise returned)");
        setJoining(false);
      }
    } catch (err) {
      console.error("Error setting up ZEGO room:", err);
      setJoinError(err.message || "Failed to initialize video call.");
      setJoining(false);
    }

    return () => {
      if (zpRef.current) {
        try { zpRef.current.destroy(); } catch (_) {}
        zpRef.current = null;
      }
    };
  }, [zegoReady, roomId, userName]);

  if (!zegoReady) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: 480,
        background: "#f0f4f8",
        borderRadius: 8,
        color: "#555",
        fontSize: 14,
        gap: 10,
      }}>
        <span style={{ fontSize: 20 }}>⏳</span> Loading video call library...
      </div>
    );
  }

  if (joinError) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: 480,
        background: "#fff3e0",
        border: "1px solid #ffb74d",
        borderRadius: 8,
        color: "#e65100",
        fontSize: 13,
        textAlign: "center",
        padding: 24,
        gap: 8,
      }}>
        <span style={{ fontSize: 24 }}>⚠️</span>
        <div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Video Call Error</div>
          <div>{joinError}</div>
        </div>
      </div>
    );
  }

  if (joining) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: 480,
        background: "#e3f2fd",
        borderRadius: 8,
        color: "#1976d2",
        fontSize: 14,
        gap: 10,
      }}>
        <span style={{ fontSize: 20 }}>🔄</span> Joining video room...
      </div>
    );
  }

  return <div ref={containerRef} style={{ width: "100%", height: "520px" }} />;
};

export default Room;
